"use client";

import { useState } from "react";
import { Reveal } from "./Reveal";
import { SectionHeader } from "./SectionHeader";
import { Lightbox } from "./Lightbox";
import { IMG, MAISON, OEUVRES, type Oeuvre } from "@/lib/diwline-data";
import { Gem, HandHeart, Leaf, Users } from "lucide-react";

const ARTWORKS_II = [
  OEUVRES.find(o => o.titre.includes("Champs d'Arachide"))!,
  OEUVRES.find(o => o.titre.includes("Palmistes"))!,
  OEUVRES.find(o => o.titre.includes("Agricultultrice"))!,
  OEUVRES.find(o => o.titre.includes("Sésame") && o.categorie === "Agriculture")!,
  OEUVRES.find(o => o.titre.includes("Offrande Royale"))!,
];

const VALEURS = [
  {
    icone: Gem,
    titre: "Excellence de Cour",
    texte: "Chaque goutte est élevée au rang d'œuvre : sélection noble, maîtrise scientifique, cachet royal.",
  },
  {
    icone: HandHeart,
    titre: "Émancipation Féminine",
    texte: "Des milliers de femmes productrices formées, rémunérées dignement, élevées au rang de Reines Nourricières.",
  },
  {
    icone: Leaf,
    titre: "Terroir Béni",
    texte: "Les oléagineux les plus nobles du Sénégal — arachide, sésame, palmiste, karité, tournesol — cultivés en agrobiologie.",
  },
  {
    icone: Users,
    titre: "Communion Nationale",
    texte: "De la coopérative villageoise à la table des grands : l'or liquide relie les fils de la Nation.",
  },
];

export function OrLiquide() {
  const [oeuvre, setOeuvre] = useState<Oeuvre | null>(null);
  const huilerie = OEUVRES.find(o => o.titre.includes("Cuves Dorées"))!;

  return (
    <section id="or-liquide" className="relative bg-royal py-24 md:py-32 bg-arabesque" aria-label="L'Or Liquide du Sénégal">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          numero="II"
          titre="L'Or Liquide du Sénégal"
          intro="Notre huilerie royale moderne marie l'ingéniosité africaine et islamique, l'innovation scientifique et la tradition ancestrale pour transformer les oléagineux nobles du Sénégal en huiles prestige reconnues sur tous les continents."
        />

        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <div className="relative">
              <button
                type="button"
                onClick={() => setOeuvre(huilerie)}
                className="gold-frame group block w-full overflow-hidden text-left"
                aria-label={`Agrandir : ${huilerie.titre}`}
              >
                <img
                  src={huilerie.src}
                  alt={huilerie.titre}
                  className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute bottom-4 left-4 rounded-sm bg-black/60 px-4 py-2 text-xs uppercase tracking-[0.25em] text-gold-light backdrop-blur-sm">
                  Cuves dorées · Flux d&apos;or pur
                </span>
              </button>
              <div className="absolute -bottom-8 -right-4 md:-right-8 rounded-sm border border-gold/40 bg-royal-panel px-6 py-5 shadow-[0_18px_50px_rgba(0,0,0,0.5)] animate-float">
                <p className="font-display text-3xl text-gold-gradient animate-shimmer">1958</p>
                <p className="mt-1 text-[10px] uppercase tracking-[0.3em] text-cream/60">
                  {MAISON.fondation}
                </p>
              </div>
            </div>
          </Reveal>

          <div>
            <Reveal delay={0.1}>
              <p className="font-elegant text-xl md:text-2xl italic leading-relaxed text-gold-light">
                « Cuves dorées, techniciens royaux, flux d&apos;or pur — chaque détail est digne
                d&apos;un patrimoine de cour. »
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-sm md:text-base leading-relaxed text-cream/80">
                Dans la grande halle des cuves, l&apos;acier précieux reflète les gestes séculaires
                des maîtres presseurs. Les graines choisies par les coopératives du royaume
                parcourent un cheminement d&apos;honneur : tri à la main, pressions douces à froid,
                filtration sur sable fin et repos en cuves d&apos;or sous la garde de techniciens
                royaux diplômés des plus grandes écoles.
              </p>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-cream/80">
                Chaque lot reçoit le sceau DIW&apos;LINE après une triple dégustation de cour —
                pureté, parfum, robe. C&apos;est cette exigence, héritée de{" "}
                {MAISON.souveraine}, qui porte aujourd&apos;hui l&apos;or liquide sénégalais des
                champs de Thiès aux tables étoilées des cinq continents.
              </p>
            </Reveal>

            <div className="mt-10 grid gap-5 sm:grid-cols-2">
              {VALEURS.map((v, i) => (
                <Reveal key={v.titre} delay={0.15 + i * 0.08}>
                  <div className="group h-full rounded-sm border border-gold/15 bg-royal-brown/80 p-5 transition-colors hover:border-gold/45 hover:bg-royal-panel">
                    <v.icone className="h-6 w-6 text-gold" aria-hidden />
                    <h3 className="mt-3 font-display text-base font-semibold text-gold-light">
                      {v.titre}
                    </h3>
                    <p className="mt-2 text-[13px] leading-relaxed text-cream/70">{v.texte}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        {/* Bandeau des œuvres d'agriculture (section II) */}
        <Reveal className="mt-20">
          <p className="mb-6 text-center font-elegant text-sm uppercase tracking-[0.4em] text-gold/70">
            Les œuvres des champs dorés
          </p>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
            {ARTWORKS_II.map((o, i) => (
              <button
                key={o.src}
                type="button"
                onClick={() => setOeuvre(o)}
                className="group relative overflow-hidden rounded-sm border border-gold/20 focus-visible:outline-gold"
                aria-label={`Agrandir : ${o.titre}`}
                style={{ transitionDelay: `${i * 30}ms` }}
              >
                <img
                  src={o.src}
                  alt={o.titre}
                  className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <span className="absolute inset-0 flex items-end bg-gradient-to-t from-black/85 via-transparent to-transparent p-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  <span className="text-left text-[11px] font-medium leading-tight text-gold-light">
                    {o.titre}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </Reveal>
      </div>

      <Lightbox oeuvre={oeuvre} onClose={() => setOeuvre(null)} />
    </section>
  );
}
